"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const RULES = [
  {
    topic: "Subscription gate",
    rule: "Shares its plan limit with Branch and Division — creating a Warehouse counts against the same Warehouse feature cap",
  },
  {
    topic: "Unique name",
    rule: "Only needs to be unique within the same Branch, not across your whole company",
  },
  {
    topic: "Warehouse Code",
    rule: "A display label pre-filled by the form, not a value you can rely on as unique",
  },
  {
    topic: "No address, no bin-rack tracking",
    rule: "A warehouse is only ever a name under a Branch. There's no sub-location or rack-level tracking to set up",
  },
  {
    topic: "Division and Branch lock after creation",
    rule: "Editing a warehouse only lets you change its name — you can't move it to a different Branch afterward",
  },
  {
    topic: "Deactivating",
    rule: "Blocked while the warehouse has any open Quote, Sales Order, Delivery Challan, Invoice, Purchase Order, GRPO, Bill, Credit Note, Debit Note, active Recurring Invoice, Recurring Bill, or Recurring Expense, in-progress Production Order, pending Inventory Transfer Request, or draft Inventory Adjustment",
  },
  {
    topic: "Deleting",
    rule: "Far stricter — blocked by any reference at all, in any status, across essentially every document and stock record the warehouse has ever touched",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. A Warehouse is the lowest level of the location hierarchy" },
      { heading: "2. Selecting a warehouse genuinely drives your stock" },
      { heading: "3. Deactivating checks for open documents, not history" },
      { heading: "4. Deleting is far stricter than deactivating" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p key={item.term} className="text-[16px] geist-regular text-[#374151] leading-relaxed">
        <span className="geist-medium text-[#111827]">{item.term}:</span> {item.desc}
      </p>
    ))}
  </div>
);

export default function WarehouseMasterPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        {/* Header */}
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Warehouse Master
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone setting up the physical stock locations that sit under each Branch
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            A <span className="geist-medium text-[#111827]">Warehouse</span> is
            the bottom level of the Division, Branch, Warehouse hierarchy, and
            it&apos;s deliberately simple, a name under a Branch. What makes it
            important isn&apos;t the setup screen, it&apos;s that every stock
            movement in the system is keyed to a warehouse.
          </p>
        </motion.div>

        <hr className="my-8" />

        {/* What this module does */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          {/* 1. Hierarchy */}
          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. A Warehouse is the lowest level of the location hierarchy
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            It sits directly under a <span className="geist-medium text-[#111827]">Branch</span>,
            which sits under a <span className="geist-medium text-[#111827]">Division</span>.
          </p>
          <Definitions
            items={[
              {
                term: "Warehouse Name",
                desc: "Only needs to be unique within its own Branch. The same name can exist in a different Branch without conflict.",
              },
              {
                term: "Warehouse Code",
                desc: "Shown pre-filled the same way Division and Branch codes work, but it's a display label, not something you should rely on as unique.",
              },
              {
                term: "Address, bin racks, zones",
                desc: "Don't exist. A warehouse is only ever a name attached to a Branch, with no sub-location or rack-level tracking to configure.",
              },
              {
                term: "Editing later",
                desc: "Only the name can be changed. Division and Branch are locked in once the warehouse is created.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: the Add Warehouse form, showing Division, Branch, and Warehouse Name only." />

          {/* 2. Stock */}
          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Selecting a warehouse genuinely drives your stock
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Every transaction that moves stock does so against the specific
            warehouse you pick.
          </p>
          <Definitions
            items={[
              {
                term: "GRPO",
                desc: "Adds stock to the warehouse you select on the receipt.",
              },
              {
                term: "Invoice, Delivery Challan, Bill",
                desc: "Deduct or add stock from the warehouse selected on that document.",
              },
              {
                term: "A hidden system warehouse",
                desc: "Exists behind the scenes for companies with warehouse-splitting turned off. It's excluded from every warehouse picker in the app, so you'll never see or select it directly.",
              },
            ]}
          />
          <Callout type="note">
            If a warehouse you expect to see is missing from a dropdown,
            it&apos;s worth checking whether it&apos;s one of these
            system-reserved warehouses rather than assuming it wasn&apos;t
            created.
          </Callout>
          <Screenshot caption="Screenshot: a GRPO receipt with a Warehouse field, showing where stock actually lands." />

          {/* 3. Deactivate */}
          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Deactivating checks for open documents, not history
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            The same shape of check used on Division and Branch, run against
            this warehouse.
          </p>
          <Definitions
            items={[
              {
                term: "Blocked while open",
                desc: "Any open Quote, Sales Order, Delivery Challan, Invoice, Purchase Order, GRPO, Bill, Credit Note, or Debit Note, an active Recurring Invoice, Bill, or Expense, an in-progress Production Order, a pending Inventory Transfer Request, or a draft Inventory Adjustment stops the deactivation.",
              },
              {
                term: "Reactivating",
                desc: "Has no equivalent check. You can turn a warehouse back on any time.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: the Mark as Inactive action in a warehouse row's Actions menu." />

          {/* 4. Delete */}
          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Deleting is far stricter than deactivating
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Where deactivate only cares about what&apos;s currently open,
            delete cares whether the warehouse was ever used at all.
          </p>
          <Callout type="warning">
            Once a warehouse has any transaction or stock history at all,
            even fully closed or completed, it effectively can&apos;t be
            deleted. Deactivate is the realistic option at that point.
          </Callout>
          <Screenshot caption="Screenshot: the delete confirmation blocked by an in-use warehouse." />
        </motion.div>

        <hr className="my-8" />

        {/* Business rules */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr key={r.topic} className={i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""}>
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">{r.rule}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Related */}
        <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4">
          <Link
            href="/documentation/books/all-master/division"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Division <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              The top of the same hierarchy
            </p>
          </Link>
          <Link
            href="/documentation/books/all-master/branch"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Branch <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              The direct parent every Warehouse is created under
            </p>
          </Link>
          <Link
            href="/documentation/books/all-master/item-master"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Item Master <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              Where each item's stock per warehouse actually lives
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
